import { FC, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Theme, Grid, Button, Divider, MenuItem } from '@mui/material';
import { FilterList, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { SMBSelect } from '../../../components';
import { IDropdownResponse } from '../../../models';

interface IUserManagementFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setSelectedUserManagementVendor?: (val: string | number) => void;
  selectedUserManagementVendor?: string | number;
  userManagementVendorOptions: IDropdownResponse[];
}

export const UserManagementFilters: FC<IUserManagementFilters> = ({
  isLoading,
  applyFilters,
  setSelectedUserManagementVendor,
  selectedUserManagementVendor,
  userManagementVendorOptions
}) => {
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);

  const classes = useStyles({ isMobileFilterButtonOpen });

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={3}>
          <SMBSelect
            label="Vendor"
            disabled={isLoading}
            value={String(selectedUserManagementVendor)}
            onChange={({ target: { value } }) => {
              setSelectedUserManagementVendor(value as string);
            }}
          >
            {userManagementVendorOptions &&
              userManagementVendorOptions.map((vendorOption, index) => {
                return (
                  <MenuItem key={`${index}`} value={vendorOption.value}>
                    {vendorOption.description}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} md={5}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
